import { usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'

/** Final market tags supplied by the server, including all four shop currencies. */
export type Prices = Partial<Record<string, number>>

/** Format authoritative unit tags; totals sum units instead of re-rounding a basket. */
export function useCurrency() {
  const { currency, locale } = usePage<SharedProps>().props
  const meta = currency.currencies.find(c => c.code === currency.current) ?? currency.currencies[0]
  const rate = currency.rates[currency.current] ?? 1

  /** Compatibility for non-product USD amounts; product tags come from the server. */
  const convert = (usd: number): number => Math.ceil(Number((usd * rate / meta.step).toFixed(12))) * meta.step

  /** Preserve the sum of final unit prices, including mixed legacy callers. */
  const unit = (usd: number, prices?: Prices): number => prices?.[currency.current] ?? convert(usd)

  const render = (amount: number): string => {
    const digits = Math.abs(amount - Math.round(amount)) > 0.004 ? 2 : 0
    const number = new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount)
    return currency.current === 'USD' ? `${meta.symbol}${number}` : `${number} ${meta.symbol}`
  }

  const format = (usd: number, prices?: Prices, qty = 1): string => render(unit(usd, prices) * qty)
  const subtotal = (lines: { price: number; prices?: Prices; quantity: number }[]): number =>
    lines.reduce((sum, line) => sum + unit(line.price, line.prices) * line.quantity, 0)

  const formatTotal = (lines: { price: number; prices?: Prices; quantity: number }[]): string => render(subtotal(lines))
  const formatTotalWithExtra = (lines: { price: number; prices?: Prices; quantity: number }[], extra: { price: number; prices?: Prices }): string =>
    render(subtotal(lines) + unit(extra.price, extra.prices))

  return {
    current: currency.current,
    isDefault: currency.current === currency.default,
    currencies: currency.currencies,
    shippingPrices: currency.shippingPrices,
    convert,
    format,
    formatTotal,
    formatTotalWithExtra,
  }
}
